/**
 * Page-specific skeletons. Each mirrors the exact structure (columns, spacing,
 * height) of its target page so the transition to real content is invisible.
 * Rule: match the wrapper (hm-surface / grid gap / rounded corners) 1:1.
 */
import { Skeleton } from "@/components/ui/skeleton";

/** Companies table skeleton — 5 columns matching the real thead. */
export function CompaniesTableSkeleton({ rows = 12 }) {
  return (
    <div className="hm-surface overflow-hidden" data-testid="skeleton-companies-table">
      <table className="w-full text-sm">
        <thead className="border-b border-hm-border bg-hm-bg-1">
          <tr>
            {["Name", "Priority", "Regions", "Topics", "Site"].map((h) => (
              <th
                key={h}
                className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className="border-b border-hm-border">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-hm-bg-2" />
                  <Skeleton className="h-3.5 w-40" />
                </div>
                <Skeleton className="h-2.5 w-72 mt-2" />
              </td>
              <td className="px-4 py-3"><Skeleton className="h-2.5 w-16" /></td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  <Skeleton className="h-4 w-10 rounded-full" />
                  <Skeleton className="h-4 w-14 rounded-full" />
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  <Skeleton className="h-4 w-12 rounded-full" />
                  <Skeleton className="h-4 w-16 rounded-full" />
                  <Skeleton className="h-4 w-10 rounded-full" />
                </div>
              </td>
              <td className="px-4 py-3"><Skeleton className="h-3 w-12" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Editorial proposal cards skeleton — 2-col grid matching ProposalCard. */
export function EditorialGridSkeleton({ n = 6 }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="skeleton-editorial-grid">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="hm-surface p-5 flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <Skeleton className="h-5 w-5 rounded shrink-0" />
            <div className="flex-1 min-w-0">
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-3 w-full mt-2" />
              <Skeleton className="h-3 w-11/12 mt-1.5" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-14" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-14 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
          <Skeleton className="h-2.5 w-24" />
          <div className="flex items-center gap-2 pt-2 border-t border-hm-border">
            <Skeleton className="h-7 w-32 rounded-md" />
            <Skeleton className="h-7 w-16 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Editorial KPI header skeleton (4 tiles). */
export function EditorialKpisSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="skeleton-editorial-kpis">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="hm-surface p-4">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="h-7 w-16 mt-2" />
        </div>
      ))}
    </div>
  );
}

/** Sources page skeleton — territory groups with 3-col grid of source cards. */
export function SourcesSkeleton() {
  return (
    <div className="space-y-8" data-testid="skeleton-sources">
      {Array.from({ length: 3 }).map((_, gi) => (
        <div key={gi}>
          <div className="flex items-center gap-2 mb-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-10" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, si) => (
              <div key={si} className="hm-surface p-4">
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="h-2.5 w-52 mt-2" />
                <div className="flex gap-1.5 mt-3">
                  <Skeleton className="h-4 w-14 rounded-full" />
                  <Skeleton className="h-4 w-12 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Daily Briefs list skeleton — row per brief. */
export function DailyBriefsSkeleton({ rows = 8 }) {
  return (
    <div className="space-y-2" data-testid="skeleton-daily-briefs">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="hm-surface p-4 flex items-center gap-4">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-5 w-16 rounded-full ml-2" />
          <Skeleton className="h-2.5 w-24 ml-auto" />
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      ))}
    </div>
  );
}

/** Knowledge page skeleton — 2-col graph + list layout. */
export function KnowledgeSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="skeleton-knowledge">
      <div className="hm-surface p-4 lg:col-span-2 h-[420px] flex items-center justify-center">
        <Skeleton className="h-40 w-40 rounded-full" />
      </div>
      <div className="hm-surface p-4 space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-md" />
            <div className="flex-1 min-w-0">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-2.5 w-24 mt-1" />
            </div>
            <Skeleton className="h-3 w-8" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Memory page skeleton — 4 KPI + 3 sections of entries. */
export function MemorySkeleton() {
  return (
    <div className="space-y-6" data-testid="skeleton-memory">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="hm-surface p-4">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-6 w-12 mt-2" />
          </div>
        ))}
      </div>
      {Array.from({ length: 3 }).map((_, s) => (
        <div key={s}>
          <Skeleton className="h-4 w-32 mb-3" />
          <div className="hm-surface divide-y divide-hm-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-3 flex items-center gap-3">
                <Skeleton className="h-3 w-3 rounded-full" />
                <div className="flex-1 min-w-0">
                  <Skeleton className="h-3 w-56" />
                  <Skeleton className="h-2.5 w-40 mt-1" />
                </div>
                <Skeleton className="h-2.5 w-16" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Agents page skeleton — list of agent cards with meta chips. */
export function AgentsSkeleton({ n = 8 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="skeleton-agents">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="hm-surface p-4">
          <div className="flex items-start gap-3">
            <Skeleton className="h-8 w-8 rounded-md" />
            <div className="flex-1 min-w-0">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-2.5 w-56 mt-2" />
              <div className="flex gap-1.5 mt-3">
                <Skeleton className="h-4 w-14 rounded-full" />
                <Skeleton className="h-4 w-16 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Workflows list skeleton — same layout as Agents. */
export function WorkflowsSkeleton({ n = 6 }) {
  return <AgentsSkeleton n={n} />;
}

/** Logs virtual list skeleton — dense rows. */
export function LogsSkeleton({ rows = 14 }) {
  return (
    <div className="hm-surface overflow-hidden" data-testid="skeleton-logs">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="border-b border-hm-border px-4 py-2.5 flex items-center gap-3">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-4 w-12 rounded-full" />
          <Skeleton className="h-2.5 w-32" />
          <Skeleton className="h-2.5 w-56" />
          <Skeleton className="h-2.5 w-14 ml-auto" />
        </div>
      ))}
    </div>
  );
}

/** Settings tab body skeleton — 5 form rows. Reused across every tab. */
export function SettingsTabSkeleton() {
  return (
    <div className="hm-surface p-6 space-y-4" data-testid="skeleton-settings-tab">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-24" />
          <Skeleton className="h-8 w-full max-w-lg" />
        </div>
      ))}
    </div>
  );
}
